import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useHomeLayout } from '../hooks/useHomeLayout';
import type { HomeLayoutItem } from '../hooks/useHomeLayout';
import { WARM, LICHT_KERN } from '../theme/licht';

/**
 * De lampen en sferen die de bewoner op zijn BEGINSCHERM heeft gezet.
 *
 * ── WAAROM DIT EEN EIGEN COMPONENT IS, EN NIET HET KNOPPENRASTER ────────────
 *
 * "Op het beginscherm" landde eerst in `DynamicButtonGrid`. Dat raster hangt onder
 * `WidgetGrid`, en die staat op de **AppsPage** — niet op HomePage. De knop kwam dus
 * netjes op een scherm terecht, alleen niet op het scherm dat de bewoner "beginscherm"
 * noemt. Een fout in mijn aanname, niet in de opslag.
 *
 * HomePage is de fotolijst met de klok en de glazen knoppen. Daar horen deze bij, in
 * dezelfde kolom als Noodoproep, Deur openen en Domotica.
 *
 * ── EEN STROOK, GEEN RIJ ────────────────────────────────────────────────────
 *
 * De kolom heeft de noodknop op ¾ van de hoogte, en die moet dominant blijven: dat is de
 * knop waar het scherm voor bestaat. Favorieten komen er daarom NAAST elkaar in één
 * strook van dezelfde hoogte als de deurknop, in plaats van elk een eigen rij. Meer dan
 * vier passen niet leesbaar op 800x480, dus daarboven tonen we er vier — de rest blijft
 * op de domoticapagina staan.
 *
 * ── HETZELFDE LICHT, MAAR OP GLAS ───────────────────────────────────────────
 *
 * De domoticapagina laat de kaart oplichten. Hier ligt de knop op een foto, dus een
 * warme wassing zou vuil worden. In plaats daarvan wordt het GLAS warm: dezelfde
 * amberkleur, als tint in het materiaal en als gloed eromheen.
 */

const MAX_ZICHTBAAR = 4;

/** Het materiaal van de knoppen op HomePage. Letterlijk hetzelfde, anders valt hij op. */
const GLAS_SHEEN = 'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, '
  + 'rgba(255,255,255,0.08) 32%, rgba(255,255,255,0) 60%)';

export function HomeFavourites() {
  const { api } = useAuth();
  const { layout, reload } = useHomeLayout();
  const [bezig, setBezig] = useState<string | null>(null);

  // Alleen lampen en sferen. Paginaknoppen hebben op HomePage hun eigen vaste plek
  // (Domotica, Deur openen, Agenda) en staan verder op de Apps-pagina.
  const favorieten = useMemo(
    () => layout.items.filter((i) => i.kind === 'output' || i.kind === 'scene').slice(0, MAX_ZICHTBAAR),
    [layout.items],
  );

  const druk = useCallback(async (item: HomeLayoutItem) => {
    if (bezig) return;
    setBezig(item.key);
    try {
      if (item.kind === 'scene') {
        await api.post(`/api/v1/domotics/scenes/${item.sceneId}/trigger`, {});
      } else {
        // Een dimmer krijgt 100/0 en geen boolean: de uitgang is analoog, en `true` komt
        // daar als 1 aan — dat is niet hetzelfde als vol. Zelfde regel als op de
        // domoticapagina.
        const aan = typeof item.value === 'boolean' ? item.value : Number(item.value ?? 0) > 0;
        const waarde = item.capability === 'dimmer' ? (aan ? 0 : 100) : !aan;
        await api.post(`/api/v1/domotics/outputs/${item.outputId}/command`, { value: waarde });
      }
      // Opnieuw ophalen in plaats van de knop alvast om te kleuren. Optimistisch kleuren
      // is precies de fout die spec 20260817-mqtt-command-ack heeft opgeruimd.
      reload();
    } catch {
      /* stil: de volgende verversing toont de echte toestand */
    } finally {
      setBezig(null);
    }
  }, [api, bezig, reload]);

  if (favorieten.length === 0) return null;

  return (
    <div style={{ flex: 1, minHeight: 96, display: 'flex', gap: 12 }}>
      {favorieten.map((item) => {
        const isSfeer = item.kind === 'scene';
        const aan = !isSfeer && (typeof item.value === 'boolean'
          ? item.value
          : Number(item.value ?? 0) > 0);
        const sterkte = !aan ? 0
          : item.capability === 'dimmer' ? Math.max(0.25, Number(item.value ?? 100) / 100)
            : 1;

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => druk(item)}
            disabled={bezig === item.key}
            aria-label={item.label || 'Favoriet'}
            aria-pressed={isSfeer ? undefined : aan}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '8px 6px',
              borderRadius: 24,
              background: GLAS_SHEEN + ', '
                + (aan
                  ? `color-mix(in srgb, ${LICHT_KERN} 46%, transparent)`
                  : isSfeer
                    ? 'color-mix(in srgb, var(--caro-primary) 45%, transparent)'
                    : 'rgba(255,255,255,0.12)'),
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: `1px solid ${aan ? 'rgba(255,226,170,0.75)' : 'rgba(255,255,255,0.3)'}`,
              color: 'white',
              fontWeight: 700,
              textShadow: '0 1px 6px rgba(0,0,0,0.45)',
              cursor: bezig === item.key ? 'wait' : 'pointer',
              opacity: bezig === item.key ? 0.7 : 1,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 8px rgba(0,0,0,0.28), '
                + (aan
                  ? `0 10px 32px ${`rgba(217,130,15,${0.30 + sterkte * 0.30})`}`
                  : '0 10px 32px rgba(0,0,0,0.32)'),
              transition: 'background 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 'calc(var(--font-heading) * 1.15)', lineHeight: 1 }}>
              {item.icon || (isSfeer ? '✨' : '💡')}
            </span>
            <span
              style={{
                fontSize: 'var(--font-small)',
                lineHeight: 1.15,
                textAlign: 'center',
                // Twee regels, dan afkappen. Deze knoppen zijn smal; een naam die
                // uitloopt zou over de buurknop heen vallen.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              {item.label}
            </span>
            {item.capability === 'dimmer' && aan && (
              <span style={{ fontSize: 'var(--font-small)', color: WARM, fontWeight: 800 }}>
                {Number(item.value)}%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
